/**
 * HTTP Status Codes and Methods Reference
 * Canonical dataset for HTTP Reference page
 */

export const HTTP_STATUS_CODES = [
  {
    code: 100,
    name: 'Continue',
    category: '1xx',
    shortMeaning: 'Request headers received, client should continue.',
    commonCauses: ['Client sent Expect: 100-continue header'],
    typicalFixes: ['Usually no action; complete the request body.'],
  },
  {
    code: 101,
    name: 'Switching Protocols',
    category: '1xx',
    shortMeaning: 'Server is switching protocols as requested.',
    commonCauses: ['Upgrade to WebSocket or HTTP/2'],
    typicalFixes: ['Ensure Upgrade headers and protocol negotiation are correct.'],
  },
  {
    code: 102,
    name: 'Processing',
    category: '1xx',
    shortMeaning: 'Server has accepted the request but not completed.',
    commonCauses: ['Long-running WebDAV or asynchronous operations'],
    typicalFixes: ['Generally informational; ensure long tasks are monitored.'],
  },
  {
    code: 103,
    name: 'Early Hints',
    category: '1xx',
    shortMeaning: 'Server is sending hints about resources to preload.',
    commonCauses: ['Server optimization for resource hints'],
    typicalFixes: ['No action needed; informational only.'],
  },
  {
    code: 200,
    name: 'OK',
    category: '2xx',
    shortMeaning: 'Request succeeded.',
    commonCauses: ['Normal successful responses'],
    typicalFixes: ['None; expected outcome.'],
  },
  {
    code: 201,
    name: 'Created',
    category: '2xx',
    shortMeaning: 'Resource successfully created.',
    commonCauses: ['POST to create resource'],
    typicalFixes: ['Return Location header and representation if needed.'],
  },
  {
    code: 202,
    name: 'Accepted',
    category: '2xx',
    shortMeaning: 'Request accepted for processing but not completed.',
    commonCauses: ['Asynchronous processing'],
    typicalFixes: ['Provide status endpoint or polling mechanism.'],
  },
  {
    code: 203,
    name: 'Non-Authoritative Information',
    category: '2xx',
    shortMeaning: 'Metadata from a transformed or cached copy.',
    commonCauses: ['Proxy or cache transformation'],
    typicalFixes: ['Verify source if authoritative data is required.'],
  },
  {
    code: 204,
    name: 'No Content',
    category: '2xx',
    shortMeaning: 'Request succeeded but no content to return.',
    commonCauses: ['DELETE or PUT with no response body'],
    typicalFixes: ['None; expected for certain operations.'],
  },
  {
    code: 206,
    name: 'Partial Content',
    category: '2xx',
    shortMeaning: 'Partial content returned due to Range header.',
    commonCauses: ['Range requests for large files'],
    typicalFixes: ['Ensure Range header handling is correct.'],
  },
  {
    code: 207,
    name: 'Multi-Status',
    category: '2xx',
    shortMeaning: 'Multiple status codes for multiple operations.',
    commonCauses: ['WebDAV batch operations'],
    typicalFixes: ['Check individual operation results in response body.'],
  },
  {
    code: 208,
    name: 'Already Reported',
    category: '2xx',
    shortMeaning: 'Members already enumerated in previous response.',
    commonCauses: ['WebDAV DAV:propstat'],
    typicalFixes: ['No action needed.'],
  },
  {
    code: 226,
    name: 'IM Used',
    category: '2xx',
    shortMeaning: 'Instance manipulation applied to response.',
    commonCauses: ['Delta encoding in HTTP'],
    typicalFixes: ['No action needed.'],
  },
  {
    code: 300,
    name: 'Multiple Choices',
    category: '3xx',
    shortMeaning: 'Multiple options available for the resource.',
    commonCauses: ['Content negotiation'],
    typicalFixes: ['Client should choose appropriate option.'],
  },
  {
    code: 301,
    name: 'Moved Permanently',
    category: '3xx',
    shortMeaning: 'Resource permanently moved to new URL.',
    commonCauses: ['URL restructuring', 'Domain migration'],
    typicalFixes: ['Update bookmarks and links; use Location header.'],
  },
  {
    code: 302,
    name: 'Found',
    category: '3xx',
    shortMeaning: 'Resource temporarily moved to different URL.',
    commonCauses: ['Temporary redirect'],
    typicalFixes: ['Follow Location header; may change in future.'],
  },
  {
    code: 303,
    name: 'See Other',
    category: '3xx',
    shortMeaning: 'Redirect to another resource via GET.',
    commonCauses: ['POST redirect to result page'],
    typicalFixes: ['Follow Location header with GET request.'],
  },
  {
    code: 304,
    name: 'Not Modified',
    category: '3xx',
    shortMeaning: 'Resource not modified since last request.',
    commonCauses: ['Conditional GET with valid cache'],
    typicalFixes: ['Use cached version; no action needed.'],
  },
  {
    code: 307,
    name: 'Temporary Redirect',
    category: '3xx',
    shortMeaning: 'Temporary redirect preserving request method.',
    commonCauses: ['Temporary URL change'],
    typicalFixes: ['Follow Location header with same method.'],
  },
  {
    code: 308,
    name: 'Permanent Redirect',
    category: '3xx',
    shortMeaning: 'Permanent redirect preserving request method.',
    commonCauses: ['Permanent URL change'],
    typicalFixes: ['Update URLs; follow Location header.'],
  },
  {
    code: 400,
    name: 'Bad Request',
    category: '4xx',
    shortMeaning: 'Request syntax or structure is invalid.',
    commonCauses: ['Malformed JSON', 'Missing required fields', 'Invalid parameters'],
    typicalFixes: ['Validate request body', 'Check required fields', 'Fix syntax errors.'],
  },
  {
    code: 401,
    name: 'Unauthorized',
    category: '4xx',
    shortMeaning: 'Authentication required or failed.',
    commonCauses: ['Missing or invalid credentials', 'Expired token'],
    typicalFixes: ['Include valid Authorization header', 'Refresh token', 'Re-authenticate.'],
  },
  {
    code: 403,
    name: 'Forbidden',
    category: '4xx',
    shortMeaning: 'Server understood request but refuses to authorize.',
    commonCauses: ['Insufficient permissions', 'Resource access denied'],
    typicalFixes: ['Check user permissions', 'Verify role-based access control.'],
  },
  {
    code: 404,
    name: 'Not Found',
    category: '4xx',
    shortMeaning: 'Requested resource does not exist.',
    commonCauses: ['Wrong URL', 'Deleted resource', 'Routing misconfiguration'],
    typicalFixes: ['Fix route', 'Restore resource', 'Improve 404 fallbacks.'],
  },
  {
    code: 405,
    name: 'Method Not Allowed',
    category: '4xx',
    shortMeaning: 'HTTP method not allowed for this resource.',
    commonCauses: ['Using wrong HTTP method', 'Endpoint only supports specific methods'],
    typicalFixes: ['Use correct HTTP method', 'Check Allow header for supported methods.'],
  },
  {
    code: 406,
    name: 'Not Acceptable',
    category: '4xx',
    shortMeaning: 'Server cannot produce response matching Accept header.',
    commonCauses: ['Unsupported content type requested'],
    typicalFixes: ['Adjust Accept header', 'Use supported content types.'],
  },
  {
    code: 407,
    name: 'Proxy Authentication Required',
    category: '4xx',
    shortMeaning: 'Authentication required with proxy.',
    commonCauses: ['Proxy requires credentials'],
    typicalFixes: ['Provide Proxy-Authorization header.'],
  },
  {
    code: 408,
    name: 'Request Timeout',
    category: '4xx',
    shortMeaning: 'Server timed out waiting for request.',
    commonCauses: ['Slow client', 'Network issues'],
    typicalFixes: ['Retry request', 'Optimize request size', 'Check network connectivity.'],
  },
  {
    code: 409,
    name: 'Conflict',
    category: '4xx',
    shortMeaning: 'Request conflicts with current resource state.',
    commonCauses: ['Concurrent modification', 'Duplicate creation'],
    typicalFixes: ['Resolve conflict', 'Retry with updated resource version.'],
  },
  {
    code: 410,
    name: 'Gone',
    category: '4xx',
    shortMeaning: 'Resource no longer available and will not return.',
    commonCauses: ['Permanently deleted resource'],
    typicalFixes: ['Remove references', 'Update client code.'],
  },
  {
    code: 411,
    name: 'Length Required',
    category: '4xx',
    shortMeaning: 'Content-Length header required.',
    commonCauses: ['Missing Content-Length header'],
    typicalFixes: ['Include Content-Length header in request.'],
  },
  {
    code: 412,
    name: 'Precondition Failed',
    category: '4xx',
    shortMeaning: 'Precondition in request headers failed.',
    commonCauses: ['If-Match or If-None-Match condition failed'],
    typicalFixes: ['Check resource version', 'Update conditional headers.'],
  },
  {
    code: 413,
    name: 'Payload Too Large',
    category: '4xx',
    shortMeaning: 'Request entity exceeds server limits.',
    commonCauses: ['File too large', 'Request body exceeds limit'],
    typicalFixes: ['Reduce payload size', 'Use chunked upload', 'Increase server limits.'],
  },
  {
    code: 414,
    name: 'URI Too Long',
    category: '4xx',
    shortMeaning: 'Request URI exceeds server limits.',
    commonCauses: ['Too many query parameters', 'Very long URL'],
    typicalFixes: ['Use POST for large data', 'Reduce query parameters', 'Shorten URL.'],
  },
  {
    code: 415,
    name: 'Unsupported Media Type',
    category: '4xx',
    shortMeaning: 'Media type not supported by server.',
    commonCauses: ['Wrong Content-Type header'],
    typicalFixes: ['Use supported media type', 'Check Content-Type header.'],
  },
  {
    code: 416,
    name: 'Range Not Satisfiable',
    category: '4xx',
    shortMeaning: 'Range header cannot be satisfied.',
    commonCauses: ['Invalid Range header', 'Range exceeds resource size'],
    typicalFixes: ['Fix Range header', 'Check resource size.'],
  },
  {
    code: 417,
    name: 'Expectation Failed',
    category: '4xx',
    shortMeaning: 'Expect header expectation cannot be met.',
    commonCauses: ['Server cannot satisfy Expect header'],
    typicalFixes: ['Remove or adjust Expect header.'],
  },
  {
    code: 418,
    name: "I'm a teapot",
    category: '4xx',
    shortMeaning: 'April Fools joke status code.',
    commonCauses: ['RFC 2324 joke'],
    typicalFixes: ['None; humorous status code.'],
  },
  {
    code: 422,
    name: 'Unprocessable Entity',
    category: '4xx',
    shortMeaning: 'Request well-formed but semantically invalid.',
    commonCauses: ['Validation errors', 'Business logic violations'],
    typicalFixes: ['Fix validation errors', 'Check business rules', 'Review error details.'],
  },
  {
    code: 423,
    name: 'Locked',
    category: '4xx',
    shortMeaning: 'Resource is locked.',
    commonCauses: ['WebDAV lock active'],
    typicalFixes: ['Wait for lock release', 'Contact lock owner.'],
  },
  {
    code: 424,
    name: 'Failed Dependency',
    category: '4xx',
    shortMeaning: 'Request failed due to dependency failure.',
    commonCauses: ['WebDAV dependency operation failed'],
    typicalFixes: ['Fix dependency issue', 'Retry operation.'],
  },
  {
    code: 425,
    name: 'Too Early',
    category: '4xx',
    shortMeaning: 'Request sent too early.',
    commonCauses: ['Replay attack prevention'],
    typicalFixes: ['Retry request after delay.'],
  },
  {
    code: 426,
    name: 'Upgrade Required',
    category: '4xx',
    shortMeaning: 'Client should switch to different protocol.',
    commonCauses: ['Protocol upgrade required'],
    typicalFixes: ['Upgrade protocol', 'Use HTTPS instead of HTTP.'],
  },
  {
    code: 428,
    name: 'Precondition Required',
    category: '4xx',
    shortMeaning: 'Origin server requires conditional request.',
    commonCauses: ['Missing If-Match header'],
    typicalFixes: ['Include required conditional headers.'],
  },
  {
    code: 429,
    name: 'Too Many Requests',
    category: '4xx',
    shortMeaning: 'Client has sent too many requests in a given time.',
    commonCauses: ['Rate limiting enforcement'],
    typicalFixes: ['Backoff strategy', 'Tune rate limits', 'Implement retries with jitter.'],
  },
  {
    code: 431,
    name: 'Request Header Fields Too Large',
    category: '4xx',
    shortMeaning: 'Request headers exceed server limit.',
    commonCauses: ['Too many or too large headers'],
    typicalFixes: ['Reduce header size', 'Remove unnecessary headers.'],
  },
  {
    code: 444,
    name: 'No Response (Nginx)',
    category: '4xx',
    shortMeaning: 'Server closed connection without response.',
    commonCauses: ['Nginx-specific', 'Client closed connection'],
    typicalFixes: ['Check Nginx configuration', 'Investigate connection issues.'],
  },
  {
    code: 449,
    name: 'Retry With (Microsoft)',
    category: '4xx',
    shortMeaning: 'Request should be retried with additional information.',
    commonCauses: ['Microsoft extension'],
    typicalFixes: ['Retry with additional headers or data.'],
  },
  {
    code: 451,
    name: 'Unavailable For Legal Reasons',
    category: '4xx',
    shortMeaning: 'Resource unavailable due to legal demand.',
    commonCauses: ['Legal takedown', 'DMCA notice'],
    typicalFixes: ['Contact legal team', 'Resolve legal issue.'],
  },
  {
    code: 499,
    name: 'Client Closed Request (Nginx)',
    category: '4xx',
    shortMeaning: 'Client closed connection before server responded.',
    commonCauses: ['Nginx-specific', 'Client timeout'],
    typicalFixes: ['Check client timeout settings', 'Investigate network issues.'],
  },
  {
    code: 500,
    name: 'Internal Server Error',
    category: '5xx',
    shortMeaning: 'Server encountered an unexpected condition.',
    commonCauses: ['Unhandled exceptions', 'Null pointers', 'Dependency failures'],
    typicalFixes: ['Add error handling', 'Improve logging', 'Fix server bugs.'],
  },
  {
    code: 501,
    name: 'Not Implemented',
    category: '5xx',
    shortMeaning: 'Server does not support requested functionality.',
    commonCauses: ['Method not implemented', 'Feature not available'],
    typicalFixes: ['Implement feature', 'Use alternative endpoint.'],
  },
  {
    code: 502,
    name: 'Bad Gateway',
    category: '5xx',
    shortMeaning: 'Invalid response from upstream server.',
    commonCauses: ['Upstream server error', 'Proxy configuration issue'],
    typicalFixes: ['Check upstream server', 'Fix proxy configuration.'],
  },
  {
    code: 503,
    name: 'Service Unavailable',
    category: '5xx',
    shortMeaning: 'Server temporarily unable to handle request.',
    commonCauses: ['Server overloaded', 'Maintenance mode', 'Dependency down'],
    typicalFixes: ['Retry later', 'Scale resources', 'Check dependencies.'],
  },
  {
    code: 504,
    name: 'Gateway Timeout',
    category: '5xx',
    shortMeaning: 'Upstream server did not respond in time.',
    commonCauses: ['Upstream timeout', 'Slow backend service'],
    typicalFixes: ['Increase timeout', 'Optimize upstream', 'Check backend health.'],
  },
  {
    code: 505,
    name: 'HTTP Version Not Supported',
    category: '5xx',
    shortMeaning: 'Server does not support HTTP version used.',
    commonCauses: ['Unsupported HTTP version'],
    typicalFixes: ['Use supported HTTP version', 'Upgrade server.'],
  },
  {
    code: 506,
    name: 'Variant Also Negotiates',
    category: '5xx',
    shortMeaning: 'Transparent content negotiation resulted in circular reference.',
    commonCauses: ['Content negotiation misconfiguration'],
    typicalFixes: ['Fix content negotiation configuration.'],
  },
  {
    code: 507,
    name: 'Insufficient Storage',
    category: '5xx',
    shortMeaning: 'Server unable to store representation.',
    commonCauses: ['Disk full', 'Storage quota exceeded'],
    typicalFixes: ['Free up storage', 'Increase storage quota.'],
  },
  {
    code: 508,
    name: 'Loop Detected',
    category: '5xx',
    shortMeaning: 'Infinite loop detected in request processing.',
    commonCauses: ['WebDAV loop', 'Redirect loop'],
    typicalFixes: ['Fix redirect chain', 'Break loop in configuration.'],
  },
  {
    code: 510,
    name: 'Not Extended',
    category: '5xx',
    shortMeaning: 'Server requires extensions not present in request.',
    commonCauses: ['Missing required extensions'],
    typicalFixes: ['Include required extensions in request.'],
  },
  {
    code: 511,
    name: 'Network Authentication Required',
    category: '5xx',
    shortMeaning: 'Client needs to authenticate to access network.',
    commonCauses: ['Captive portal', 'Network access control'],
    typicalFixes: ['Authenticate with network', 'Complete portal login.'],
  },
  {
    code: 598,
    name: 'Network Read Timeout Error',
    category: '5xx',
    shortMeaning: 'Network read timeout (non-standard).',
    commonCauses: ['Proxy timeout'],
    typicalFixes: ['Check network connectivity', 'Increase proxy timeout.'],
  },
  {
    code: 599,
    name: 'Network Connect Timeout Error',
    category: '5xx',
    shortMeaning: 'Network connection timed out (non-standard).',
    commonCauses: ['Upstream timeout in proxy'],
    typicalFixes: ['Optimize upstream services', 'Increase proxy timeouts sensibly.'],
  },
];

export const HTTP_METHODS = [
  {
    method: 'GET',
    safe: true,
    idempotent: true,
    cacheable: true,
    description: 'Retrieve a representation of a resource.',
  },
  {
    method: 'POST',
    safe: false,
    idempotent: false,
    cacheable: false,
    description: 'Create a subordinate resource or trigger a processing operation.',
  },
  {
    method: 'PUT',
    safe: false,
    idempotent: true,
    cacheable: false,
    description: 'Replace a resource at the target URI.',
  },
  {
    method: 'PATCH',
    safe: false,
    idempotent: false,
    cacheable: false,
    description: 'Apply partial modifications to a resource.',
  },
  {
    method: 'DELETE',
    safe: false,
    idempotent: true,
    cacheable: false,
    description: 'Remove the resource at the target URI.',
  },
  {
    method: 'HEAD',
    safe: true,
    idempotent: true,
    cacheable: true,
    description: 'Same as GET but returns only headers.',
  },
  {
    method: 'OPTIONS',
    safe: true,
    idempotent: true,
    cacheable: false,
    description: 'Describe the communication options at the target resource.',
  },
  {
    method: 'CONNECT',
    safe: false,
    idempotent: false,
    cacheable: false,
    description: 'Establish a tunnel to the server identified by the target resource.',
  },
];
